import { Component, OnDestroy, AfterViewInit, ElementRef, QueryList, ViewChildren } from '@angular/core'
import { LowerCasePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { delay, from, Subject, takeUntil } from 'rxjs'

// 1. Import only needed animations instead of all
import {
  fadeInOnEnterAnimation,
  fadeOutOnLeaveAnimation,
  headShakeAnimation,
} from 'angular-animations'

// 2. Lazy load the periodic elements data
import { SafePipe } from '@nx-superprep/shared'
import { FrontendCard as Card, Choice, ChoiceType, PartialChoice } from '@nx-superprep/backend/firebase'
import { GameplayBaseComponent, gameplayBaseStyling, gameplayReplace } from '..//gameplay-base'
import { HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser'
import { GestureConfig } from './gesture-config'
import { MatCardModule } from '@angular/material/card'

import { elements, PeriodicElement } from './elements';

interface PeriodicElementChoice extends PeriodicElement, Choice {
  selected?: boolean
}

class PartialElement extends PartialChoice<PeriodicElementChoice> {
  constructor(options: PeriodicElementChoice) {
    super({
      choice: options,
      answer: options.symbol,
      length: 1,
      value: ''
    })
  }
  override get valueLength(): number { return this.value ? 1 : 0 }
}

@Component({
  standalone: true,
  selector: 'gameplay-periodic',
  templateUrl: './periodic.component.html',
  styleUrls: ['./periodic.component.scss'],
  animations: [
    // 5. Only include animations that are actually used
    fadeInOnEnterAnimation(),
    fadeOutOnLeaveAnimation(),
    headShakeAnimation({ duration: 900, delay: 0 }),
  ],
  imports: [
    NgClass,
    NgTemplateOutlet,
    LowerCasePipe,
    SafePipe,
    MatCardModule,
    HammerModule,
  ],
  providers: [{
    provide: HAMMER_GESTURE_CONFIG,
    useClass: GestureConfig,
  }]
})
export class PeriodicComponent extends GameplayBaseComponent implements OnDestroy, AfterViewInit {
  override title = 'periodic'

  @ViewChildren('elementItem') elementItems!: QueryList<ElementRef>

  private _periodicElements: PeriodicElementChoice[] = [];
  private _periodicNames: string[] = [];
  private _periodicSymbols: string[] = [];

  get periodicElements(): PeriodicElementChoice[] {
    if (this._periodicElements.length === 0) {
      this._periodicElements = elements().map(e => ({
        ...e,
        isActive: false,
        selected: false,
        groupName: e.groupName || '',
        resources: [],
        type: ChoiceType.question,
        mimeType: '',
        animationState: '',
        name: e.name,
        isAnswer: false,
        isCorrect: false,
        isMultiple: false,
        tuple: [],
      } as unknown as PeriodicElementChoice));
    }
    return this._periodicElements;
  }

  get periodicNames(): string[] {
    if (this._periodicNames.length === 0) {
      this._periodicNames = this.periodicElements.map(e => e.name);
    }
    return this._periodicNames;
  }

  get periodicSymbols(): string[] {
    if (this._periodicSymbols.length === 0) {
      this._periodicSymbols = this.periodicElements.map(e => e.symbol);
    }
    return this._periodicSymbols;
  }

  isMultiple = false
  isNameVisible = false
  isSymbolVisible = false

  private _mouseDown = false
  private readonly onEnter = new Subject<void>()

  private _choice: Choice|undefined
  private _selectedElements: PartialElement[] = []
  private _animationCount = 0

  // Track touch positions
  private lastTouchX = 0
  private lastTouchY = 0

  // 8. Unified event handler for touch/mouse events
  private handleElementInteraction(element: HTMLElement, isStart = false) {
    if (isStart && !this._mouseDown) {
      this._mouseDown = true;
    } else if (!isStart && !this._mouseDown) {
      return;
    }

    const dataset = element.dataset;
    if (!dataset || Object.keys(dataset).length === 0) { return; }

    const group = Number(dataset['group']) || 0;
    const period = Number(dataset['period']) || 0;
    const symbol = this.periodicElements.find(
      e => e.period === period && e.group === group
    )?.symbol;

    if (symbol && (isStart || this.isMultiple)) {
      this.onClick({ symbol });
    }
  }

  // 10. Simplified event handlers
  private handleTouchMove(e: Event) {
    if (!this._mouseDown) return;

    const touchEvent = e as TouchEvent;
    this.lastTouchX = touchEvent.touches[0].clientX;
    this.lastTouchY = touchEvent.touches[0].clientY;

    requestAnimationFrame(() => {
      const element = document.elementFromPoint(this.lastTouchX, this.lastTouchY) as HTMLElement;
      if (element?.classList?.contains('element')) {
        this.handleElementInteraction(element);
      }
    });
  }

  private handleTouchStart(e: TouchEvent) {
    const target = e.target as HTMLElement;
    const element = target.closest('.element') as HTMLElement;
    if (element) {
      this.handleElementInteraction(element, true);
    }
  }

  private handleTouchEnd(p0: TouchEvent) {
    this._mouseDown = false;
  }

  private handleMouseDown(e: MouseEvent) {
    const target = e.target as HTMLElement;
    const element = target.closest('.element') as HTMLElement;
    if (element) {
      this.handleElementInteraction(element, true);
    }
  }

  private handleMouseUp(p0: MouseEvent) {
    this._mouseDown = false;
  }

  private handleMouseMove(e: MouseEvent) {
    if (!this._mouseDown) return;

    const element = (e.target as HTMLElement).closest('.element') as HTMLElement;
    if (element) {
      this.handleElementInteraction(element);
    }
  }

  onAnimationStart(e: { triggerName: string }) {
    this._animationCount = this.isMultiple && e.triggerName === 'headShake' ? 2 : 1;
  }

  override onAnimationDone(_e: unknown) {
    this._animationCount--;
  }

  get isAnimationDone() {
    return this._animationCount === 0;
  }

  get selectedValues() {
    return this._selectedElements.filter(e => e.value).map(e => e.value);
  }

  get isAnswer() {
    return this._selectedElements.every(e => e.isCorrect);
  }

  get isPrefix() {
    return this._selectedElements.every(e => e.isPrefix);
  }

  get isComplete() {
    return this._selectedElements.every(e => e.isComplete);
  }

  // Store event handler references for proper cleanup
  private eventHandlers: {[key: string]: EventListener} = {
    touchmove: (e: Event) => this.handleTouchMove(e as TouchEvent),
    touchstart: (e: Event) => this.handleTouchStart(e as TouchEvent),
    touchend: (e: Event) => this.handleTouchEnd(e as TouchEvent),
    mousedown: (e: Event) => this.handleMouseDown(e as MouseEvent),
    mouseup: (e: Event) => this.handleMouseUp(e as MouseEvent),
    mousemove: (e: Event) => this.handleMouseMove(e as MouseEvent)
  };

  ngAfterViewInit() {
    // Setup event delegation for better performance
    setTimeout(() => {
      const container = document.querySelector('.wrapper');
      if (!container) return;

      // Add all event listeners using stored references
      Object.entries(this.eventHandlers).forEach(([eventName, handler]) => {
        container.addEventListener(eventName, handler, { passive: true });
      });
    });
  }

  override ngOnDestroy() {
    // Clean up event listeners using stored references
    const container = document.querySelector('.wrapper');
    if (container) {
      Object.entries(this.eventHandlers).forEach(([eventName, handler]) => {
        container.removeEventListener(eventName, handler);
      });
    }

    this.onEnter.complete();
    this.onCancel.complete();
    super.ngOnDestroy();
  }

  override getData() {
    super.getData();
    from(this.onEnter).pipe(
      takeUntil(this.onDestroy),
      delay(500),
    ).subscribe(() => {
      this._choice = this.isAnswer
        ? this.answer
        : new Choice({type: ChoiceType.wrong, name: this.selectedValues.join('|')});
      this.onSelect(this._choice);
    });
  }

  override initState(card: Card) {
    if (!super.initState(card)) return false;

    this._animationCount = 0;
    this.isNameVisible = true;
    this.isSymbolVisible = true;

    // 12. More efficient string processing
    const questionName = this.question?.name || '';
    const nameParts = questionName.split(' ');

    this.isSymbolVisible = !nameParts.some(part => this.periodicSymbols.includes(part));
    this.isNameVisible = !nameParts.some(part => this.periodicNames.includes(part.toLowerCase()));

    this.isMultiple = !!this.answer?.isMultiple;
    const tuple = this.answer?.tuple;

    if (!tuple) {
      const answerName = this.answer?.name;
      this._selectedElements = answerName
        ? this.periodicElements
            .filter(e => e.symbol === answerName)
            .map(e => new PartialElement(e))
        : [];
    } else {
      this._selectedElements = this.periodicElements
        .filter(e => tuple.includes(e.symbol))
        .sort((a, b) => tuple.indexOf(a.symbol) < tuple.indexOf(b.symbol) ? -1 : 1)
        .map(e => new PartialElement(e));
    }

    return true;
  }

  override resetState(card: Card | undefined) {
    super.resetState(card);
    this._choice = undefined;
    this._animationCount = 0;
    this._selectedElements.forEach(e => e.reset());
    this._mouseDown = false;
  }

  onClick(element: {symbol: string}) {
    if (this.didAnswer) return;

    this.upsert(element);
    if (this.selectedValues.length > 0 && (this.isAnswer || this.isComplete)) {
      this.onEnter.next();
    }
  }

  upsert(value: { symbol: string | undefined }) {
    let found = this._selectedElements.findIndex(e => e.isValueEqualTo(value.symbol));
    if (found < 0) found = this._selectedElements.findIndex(e => !e.isComplete);
    if (found < 0) return;

    this._selectedElements[found].toggle(value.symbol);
  }

  // 13. Simplified compound text processing
  getCompound(text: string, elements: PartialElement[], isCorrect: boolean) {
    const elementsLength = elements.length;
    if (elementsLength === 0 || !text.match(gameplayBaseStyling.ellipsis)) {
      return text;
    }

    const style = isCorrect ? gameplayBaseStyling.answerRightStyle : gameplayBaseStyling.answerWrongStyle;
    const parts = text.split(gameplayBaseStyling.ellipsis);

    return parts
      .map((s, i) => s + (i < elementsLength ?
        (isCorrect ? elements[i].displayAnswer : elements[i].displayValue) : ''))
      .join('')
      .replace(gameplayReplace.answerStyle, style);
  }

  override answerText() {
    return this.getCompound(super.bottomText() as string, this._selectedElements, true);
  }

  override bottomText() {
    return this.getCompound(super.bottomText() as string, this._selectedElements, !!this._choice?.isAnswer);
  }

  getIndex(symbol: string, isAnswer = false) {
    if (!symbol) return -1;
    return this._selectedElements.findIndex(e => (isAnswer ? e?.answer : e?.value) === symbol);
  }

  isDimmed(element?: PeriodicElementChoice) {
    return this.didAnswer && !this.isCorrect(element);
  }

  isSelected(element: PeriodicElementChoice) {
    return !this.didAnswer && this.getIndex(element.symbol) >= 0;
  }

  override isActive(element: { isActive: unknown }) {
    return !this.didAnswer && !!element?.isActive;
  }

  override isChosen<T>(element: T) {
    return this.didAnswer && this.getIndex((element as unknown as PeriodicElement)?.symbol) >= 0;
  }

  override isCorrect<T>(element?: T) {
    return this.didAnswer && this.getIndex((element as unknown as PeriodicElement)?.symbol, true) >= 0;
  }

  override isAnimated(element: PeriodicElementChoice) {
    return this.isCorrect(element) && super.isAnimated(this._choice);
  }

  override onForceAnswer(isCorrect = Math.random() < 0.5) {
    this._selectedElements.forEach(e => e.forceAnswer(isCorrect));
    this.onSelect(this._choice);
  }
}
